import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { artists, allQuests, artistRewards, myArtistXP } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import QuestCard from "@/pages/quests/components/QuestCard";
import ArtistRewardCard from "@/pages/rewards/artist/components/ArtistRewardCard";
import RewardDetailModal from "@/pages/rewards/components/RewardDetailModal";
import type { Reward } from "@/mocks/artistData";

type TabKey = "overview" | "quests" | "rewards" | "ip";

export default function ArtistDetailPage() {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [followed, setFollowed] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);

  const artist = artists.find((a) => a.id === artistId);
  const quests = allQuests.filter((q) => q.artistId === artistId);
  const rewards = artistRewards.filter((r) => r.artistId === artistId);
  const xpData = myArtistXP.find((x) => x.artistId === artistId);
  const myXP = xpData?.xp ?? 0;

  if (!artist) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-white/30 mb-4 block" />
          <p className="text-white/50 mb-4">크리에이터를 찾을 수 없습니다.</p>
          <Link to="/artists" className="text-sm text-white/40 hover:text-white underline cursor-pointer">
            크리에이터 목록으로
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: string; count?: number }[] = [
    { key: "overview", label: "소개", icon: "ri-user-line" },
    { key: "quests", label: "캠페인", icon: "ri-sword-line", count: quests.length },
    { key: "rewards", label: "리워드", icon: "ri-gift-line", count: rewards.length },
    { key: "ip", label: "IP 갤러리", icon: "ri-image-line", count: artist.ipImages.length },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero Cover */}
      <div className="relative overflow-hidden pt-16">
        <div className="absolute inset-0 h-[480px]">
          <img
            src={artist.coverImage}
            alt={artist.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/40 via-[#0a0a0f]/60 to-[#0a0a0f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/70 via-transparent to-[#0a0a0f]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-0">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-10">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/artists" className="hover:text-white/70 transition-colors cursor-pointer">크리에이터</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span style={{ color: artist.accentColor }}>{artist.name}</span>
          </div>

          {/* Artist Profile Header */}
          <div className="flex flex-col lg:flex-row items-start lg:items-end gap-8 pb-0">
            {/* Avatar */}
            <div
              className="w-28 h-28 rounded-3xl overflow-hidden border-2 flex-shrink-0"
              style={{ borderColor: artist.accentColor }}
            >
              <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: `${artist.accentColor}20`, color: artist.accentColor, border: `1px solid ${artist.accentColor}40` }}
                >
                  {artist.categoryLabel}
                </span>
                {artist.verified && (
                  <span className="flex items-center gap-1 text-xs text-white/50">
                    <i className="ri-verified-badge-fill" style={{ color: artist.accentColor }} />
                    공식 인증
                  </span>
                )}
                <span className="text-xs text-white/30">데뷔 {artist.debutYear}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-2">{artist.name}</h1>
              <p className="text-white/50 text-base italic mb-4">{artist.tagline}</p>
              {/* Genre Tags */}
              <div className="flex flex-wrap gap-2">
                {artist.genre.map((g) => (
                  <span
                    key={g}
                    className="px-3 py-1 rounded-full text-xs font-medium border"
                    style={{ borderColor: `${artist.accentColor}40`, color: artist.accentColor, background: `${artist.accentColor}12` }}
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setFollowed((p) => !p)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                style={
                  followed
                    ? { background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }
                    : { background: `linear-gradient(to right, ${artist.accentColor}, ${artist.secondaryColor})`, color: "#0a0a0f" }
                }
              >
                <i className={followed ? "ri-check-line" : "ri-add-line"} />
                {followed ? "팔로잉" : "팔로우"}
              </button>
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 text-white/40 hover:text-white/70 hover:border-white/30 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-7xl mx-auto px-6 mt-8 mb-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "팔로워", value: artist.stats.followers, icon: "ri-heart-line" },
            { label: "IP 컬렉션", value: `${artist.stats.ipCollections}개`, icon: "ri-image-line" },
            { label: "캠페인", value: `${quests.length}개`, icon: "ri-sword-line" },
            { label: "리워드", value: `${rewards.length}개`, icon: "ri-gift-line" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 flex items-center gap-4"
            >
              <div
                className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ background: `${artist.accentColor}18`, border: `1px solid ${artist.accentColor}40` }}
              >
                <i className={`${s.icon} text-base`} style={{ color: artist.accentColor }} />
              </div>
              <div>
                <div className="text-white font-black text-lg">{s.value}</div>
                <div className="text-white/40 text-xs">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-8">
        <div className="flex items-center gap-1 border-b border-white/10">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "border-current"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}
              style={activeTab === tab.key ? { color: artist.accentColor, borderColor: artist.accentColor } : {}}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={
                    activeTab === tab.key
                      ? { background: `${artist.accentColor}25`, color: artist.accentColor }
                      : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }
                  }
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-10 pb-24">

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Bio */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <h2 className="text-white font-black text-lg mb-4 flex items-center gap-2">
                  <i className="ri-information-line" style={{ color: artist.accentColor }} />
                  소개
                </h2>
                <p className="text-white/60 text-sm leading-relaxed">{artist.bio}</p>
              </div>

              {/* IP Preview */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-white font-black text-lg flex items-center gap-2">
                    <i className="ri-image-line" style={{ color: artist.accentColor }} />
                    IP 갤러리 미리보기
                  </h2>
                  <button
                    onClick={() => setActiveTab("ip")}
                    className="text-xs font-semibold cursor-pointer transition-colors"
                    style={{ color: artist.accentColor }}
                  >
                    전체 보기 →
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {artist.ipImages.slice(0, 4).map((img, i) => (
                    <div key={i} className="aspect-square rounded-xl overflow-hidden border border-white/10">
                      <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Latest Quests */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-white font-black text-lg flex items-center gap-2">
                    <i className="ri-sword-line" style={{ color: artist.accentColor }} />
                    최근 캠페인
                  </h2>
                  <button
                    onClick={() => setActiveTab("quests")}
                    className="text-xs font-semibold cursor-pointer transition-colors"
                    style={{ color: artist.accentColor }}
                  >
                    전체 보기 →
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {quests.slice(0, 2).map((quest) => (
                    <QuestCard key={quest.id} quest={quest} />
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* XP Status */}
              {xpData && (
                <div
                  className="rounded-2xl p-6 border"
                  style={{ background: `${artist.accentColor}10`, borderColor: `${artist.accentColor}30` }}
                >
                  <h3 className="text-white font-black text-base mb-4 flex items-center gap-2">
                    <i className="ri-star-line" style={{ color: artist.accentColor }} />
                    내 {artist.name} XP
                  </h3>
                  <div className="text-3xl font-black mb-1" style={{ color: artist.accentColor }}>
                    {xpData.xp.toLocaleString()} XP
                  </div>
                  <div className="text-white/40 text-xs mb-4">Lv.{xpData.level} · {xpData.levelName}</div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${(xpData.xp / xpData.nextLevelXP) * 100}%`, background: artist.accentColor }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-white/30">
                    <span>{xpData.xp.toLocaleString()} XP</span>
                    <span>다음 레벨 {xpData.nextLevelXP.toLocaleString()} XP</span>
                  </div>
                </div>
              )}

              {/* Quick Links */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3">
                <h3 className="text-white font-black text-base mb-4">바로가기</h3>
                <Link
                  to={`/quests?artist=${artist.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 text-white/60 hover:text-white hover:border-white/25 transition-all cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${artist.accentColor}18` }}>
                    <i className="ri-sword-line text-sm" style={{ color: artist.accentColor }} />
                  </div>
                  <span className="text-sm font-medium">캠페인 전체 보기</span>
                  <i className="ri-arrow-right-s-line ml-auto text-white/30" />
                </Link>
                <Link
                  to={`/rewards/${artist.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 text-white/60 hover:text-white hover:border-white/25 transition-all cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${artist.accentColor}18` }}>
                    <i className="ri-gift-line text-sm" style={{ color: artist.accentColor }} />
                  </div>
                  <span className="text-sm font-medium">리워드 전체 보기</span>
                  <i className="ri-arrow-right-s-line ml-auto text-white/30" />
                </Link>
              </div>

              {/* Group Info */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <h3 className="text-white font-black text-base mb-4">기본 정보</h3>
                <div className="space-y-3">
                  {[
                    { label: "소속", value: artist.groupName },
                    { label: "데뷔", value: `${artist.debutYear}년` },
                    { label: "카테고리", value: artist.categoryLabel },
                    { label: "완료된 퀘스트", value: `${artist.stats.completedQuests.toLocaleString()}개` },
                  ].map((info) => (
                    <div key={info.label} className="flex justify-between items-center">
                      <span className="text-white/40 text-xs">{info.label}</span>
                      <span className="text-white/80 text-xs font-medium">{info.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quests Tab */}
        {activeTab === "quests" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-white font-black text-xl">{artist.name}의 캠페인</h2>
                <p className="text-white/40 text-sm mt-1">총 {quests.length}개의 캠페인</p>
              </div>
              <Link
                to={`/quests?artist=${artist.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 text-white/60 hover:text-white hover:border-white/30 text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-xs" />
                캠페인 페이지에서 보기
              </Link>
            </div>
            {quests.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {quests.map((quest) => (
                  <QuestCard key={quest.id} quest={quest} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-white/30">
                <i className="ri-sword-line text-4xl mb-4 block" />
                <p>아직 캠페인이 없습니다.</p>
              </div>
            )}
          </div>
        )}

        {/* Rewards Tab */}
        {activeTab === "rewards" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-white font-black text-xl">{artist.name}의 리워드</h2>
                <p className="text-white/40 text-sm mt-1">
                  총 {rewards.length}개 · 내 XP: <span className="font-bold" style={{ color: artist.accentColor }}>{myXP.toLocaleString()}</span>
                </p>
              </div>
              <Link
                to={`/rewards/${artist.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 text-white/60 hover:text-white hover:border-white/30 text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-xs" />
                리워드 페이지에서 보기
              </Link>
            </div>
            {rewards.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rewards.map((reward) => (
                  <ArtistRewardCard
                    key={reward.id}
                    reward={reward}
                    artist={artist}
                    myXP={myXP}
                    onSelect={() => setSelectedReward(reward)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-white/30">
                <i className="ri-gift-line text-4xl mb-4 block" />
                <p>아직 리워드가 없습니다.</p>
              </div>
            )}
          </div>
        )}

        {/* IP Gallery Tab */}
        {activeTab === "ip" && (
          <div>
            <div className="mb-6">
              <h2 className="text-white font-black text-xl">{artist.name} IP 갤러리</h2>
              <p className="text-white/40 text-sm mt-1">총 {artist.stats.ipCollections}개의 공식 IP 컬렉션</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {artist.ipImages.map((img, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-2xl overflow-hidden border border-white/10 group cursor-pointer relative"
                >
                  <img
                    src={img}
                    alt={`${artist.name} IP ${i + 1}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div
                    className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: `${artist.accentColor}25`, color: artist.accentColor, border: `1px solid ${artist.accentColor}40` }}
                  >
                    IP #{String(i + 1).padStart(2, "0")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reward Detail Modal */}
      {selectedReward && (
        <RewardDetailModal
          reward={selectedReward}
          artist={artist}
          myXP={myXP}
          onClose={() => setSelectedReward(null)}
        />
      )}
    </div>
  );
}
